import { useRef, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { X } from "@phosphor-icons/react";

/**
 * Normaliza o identificador digitado manualmente.
 *
 * Regra preservada do fluxo original do Vendedor: CNPJ é apenas "14 dígitos".
 * Qualquer outra coisa é tratada como Inscrição Estadual e passa adiante crua,
 * sem validação. Não endurecer sem decisão de produto — a mesa usa IE de
 * formatos variados por estado.
 */
export function normalizarCnpjIe(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, "");
  const isCnpj = digits.length === 14;
  const formatted = isCnpj
    ? `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
    : trimmed;
  return { formatted, label: `${isCnpj ? "CNPJ" : "IE"}: ${formatted}` };
}

/**
 * Campo Cliente com busca no SAP e fallback manual por CNPJ/IE.
 *
 * Quatro estados, nesta ordem de precedência:
 *   1. `value`      -> cliente do SAP selecionado (chip verde, com BP)
 *   2. `manualId`   -> identificador manual confirmado (chip âmbar, sem BP)
 *   3. modo CNPJ/IE -> input do identificador manual
 *   4. busca        -> autocomplete + dropdown/painel de "não encontrado"
 *
 * `value` e `manualId` são controlados pelo pai; o resto do estado (query,
 * resultados, modo) é interno. Para limpar o campo por fora, remonte via `key`.
 *
 * onChange recebe sempre a tripla completa: { cliente, clienteSap, manualId }.
 * `cliente` é a string que vai no payload — nome do SAP ou o CNPJ/IE formatado.
 */
export default function ClienteSapInput({
  value = null,
  manualId = null,
  onChange,
  allowManual = true,
  inputId = "cliente",
  testId = "input-cliente",
  placeholder = "Nome do Cliente",
}) {
  const [sapQuery, setSapQuery] = useState(value?.nome || "");
  const [sapResults, setSapResults] = useState([]);
  const [sapLoading, setSapLoading] = useState(false);
  const [sapNoResults, setSapNoResults] = useState(false);
  const [cnpjIeMode, setCnpjIeMode] = useState(false);
  const [cnpjIeInput, setCnpjIeInput] = useState("");
  const debounceRef = useRef(null);

  const emit = (next) => onChange && onChange(next);

  const handleSapSearch = (query) => {
    setSapQuery(query);
    setSapNoResults(false);
    if (value) { emit({ cliente: "", clienteSap: null, manualId: null }); }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 3) { setSapResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSapLoading(true);
      try {
        const { data } = await api.get("/clientes/search", { params: { q: query.trim() } });
        setSapResults(data);
        setSapNoResults(data.length === 0);
      } catch { setSapResults([]); }
      finally { setSapLoading(false); }
    }, 400);
  };

  const handleSapSelect = (item) => {
    setSapQuery(item.nome);
    setSapResults([]);
    emit({ cliente: item.nome, clienteSap: item, manualId: null });
  };

  const clearCliente = () => {
    setSapQuery("");
    setSapResults([]);
    setSapNoResults(false);
    setCnpjIeMode(false);
    setCnpjIeInput("");
    emit({ cliente: "", clienteSap: null, manualId: null });
  };

  const confirmarCnpjIe = () => {
    const parsed = normalizarCnpjIe(cnpjIeInput);
    if (!parsed) return;
    emit({ cliente: parsed.formatted, clienteSap: null, manualId: parsed.label });
  };

  const abrirModoManual = () => {
    setCnpjIeMode(true);
    setSapResults([]);
    setSapNoResults(false);
  };

  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center justify-between border border-[#40B6A2]/40 bg-[#40B6A2]/5 rounded-sm px-3 py-2 min-h-[40px]">
          <div>
            <p className="text-sm font-medium" style={{ color: "#40B6A2" }}>{value.nome}</p>
            <p className="text-[11px] text-[#6b89a8] font-mono-data">
              {value.cnpj && <>{value.cnpj} · </>}BP {value.business_partner}
            </p>
          </div>
          <button type="button" onClick={clearCliente} className="text-[#6b89a8] hover:text-white ml-3 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : manualId ? (
        <div className="flex items-center justify-between border border-[#FFB800]/40 bg-[#FFB800]/5 rounded-sm px-3 py-2 min-h-[40px]">
          <div>
            <p className="text-sm font-medium font-mono-data" style={{ color: "#FFB800" }}>{manualId}</p>
            <p className="text-[11px] text-[#6b89a8]">Identificação manual</p>
          </div>
          <button type="button" onClick={clearCliente} className="text-[#6b89a8] hover:text-white ml-3 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : cnpjIeMode ? (
        <div className="space-y-1.5">
          <div className="flex gap-2 items-center">
            <Input
              autoFocus
              autoComplete="off"
              value={cnpjIeInput}
              onChange={(e) => setCnpjIeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  confirmarCnpjIe();
                }
                if (e.key === "Escape") { setCnpjIeMode(false); setCnpjIeInput(""); }
              }}
              placeholder="CNPJ ou Inscrição Estadual"
              className="bg-transparent border-[#123a5e] focus-visible:ring-[#40B6A2] flex-1"
            />
            <button type="button" onClick={() => { setCnpjIeMode(false); setCnpjIeInput(""); }} className="text-[#6b89a8] hover:text-white flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={confirmarCnpjIe}
            className="text-xs text-[#40B6A2] hover:underline"
          >
            Usar este identificador →
          </button>
        </div>
      ) : (
        <>
          <Input
            id={inputId}
            data-testid={testId}
            value={sapQuery}
            autoComplete="off"
            onChange={(e) => handleSapSearch(e.target.value)}
            onBlur={() => setTimeout(() => { setSapResults([]); setSapNoResults(false); }, 200)}
            placeholder={placeholder}
            className="bg-transparent border-[#123a5e] focus-visible:ring-[#40B6A2] pr-8"
          />
          {sapLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-[#40B6A2] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {sapResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-[#0B2D4D] border border-[#123a5e] rounded-sm shadow-xl max-h-64 overflow-y-auto">
              {sapResults.map((item) => (
                <div
                  key={item.business_partner}
                  className="px-3 py-2.5 hover:bg-[#40B6A2]/10 cursor-pointer border-b border-[#123a5e] last:border-0"
                  onMouseDown={() => handleSapSelect(item)}
                >
                  <p className="text-sm text-white">{item.nome}</p>
                  <p className="text-[11px] text-[#6b89a8] font-mono-data">
                    {item.cnpj || "—"} · BP {item.business_partner}
                  </p>
                </div>
              ))}
              {allowManual && (
                <div
                  className="px-3 py-2.5 hover:bg-[#FFB800]/10 cursor-pointer border-t border-[#123a5e]"
                  onMouseDown={abrirModoManual}
                >
                  <p className="text-xs text-[#FFB800]">Não encontrado? Informar CNPJ/IE</p>
                </div>
              )}
            </div>
          )}
          {sapNoResults && (
            <div className="absolute z-50 w-full mt-1 bg-[#0B2D4D] border border-[#123a5e] rounded-sm shadow-xl">
              {allowManual ? (
                <div
                  className="px-3 py-2.5 hover:bg-[#FFB800]/10 cursor-pointer"
                  onMouseDown={abrirModoManual}
                >
                  <p className="text-xs text-[#6b89a8]">Nenhum cliente encontrado.</p>
                  <p className="text-xs text-[#FFB800] mt-0.5">Informar CNPJ/IE →</p>
                </div>
              ) : (
                <div className="px-3 py-2.5">
                  <p className="text-xs text-[#6b89a8]">Nenhum cliente encontrado.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
